/**
 * Router (APP-001 §6, APP-002 §30, §38).
 *
 * Route skeleton:
 *   /                         → root (redirect by auth state)
 *   /sign-in | /sign-up       → Clerk <SignIn/> / <SignUp/>
 *   /public/playground        → anonymous pre-signup playground (no shell)
 *   /onboarding               → protected onboarding flow (APP-002)
 *   /dashboard                → protected overview (APP-002 composition)
 *   /playground | /api-keys | /usage | /bulk | /webhooks | /billing | /account
 *                             → protected dashboard shell + feature page
 *   *                         → 404
 *
 * All protected routes share <ProtectedRoute/>, which gates on Clerk
 * initialization, a signed-in session, AND backend account resolution, and
 * routes incomplete onboarding to /onboarding (APP-002 §30).
 *
 * Feature routes are lazy-loaded (APP-002 §38) so they do not bloat the primary
 * onboarding/dashboard chunks. The dashboard + onboarding routes remain eagerly
 * loaded for fast first paint. No feature route is a placeholder as of APP-009.
 */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { RootRoute } from '@/routes/RootRoute';
import { SignInRoute } from '@/routes/SignInRoute';
import { SignUpRoute } from '@/routes/SignUpRoute';
import { NotFoundRoute } from '@/routes/NotFoundRoute';
import { LegalRedirectRoute } from '@/routes/LegalRedirectRoute';
import { DashboardRoute } from '@/features/dashboard/DashboardRoute';
import { OnboardingRoute } from '@/features/onboarding/OnboardingRoute';
import { LoadingState } from '@/components/feedback/LoadingState';

// Feature routes are code-split (APP-002 §38) so they stay off the critical
// first-paint path. Each resolves to a real protected dashboard page; none is a
// placeholder as of APP-009.
// APP-005: the Playground page is a real protected page, code-split like the
// other dashboard features.
const PlaygroundPage = lazy(() =>
  import('@/features/playground/PlaygroundRoute').then((m) => ({ default: m.PlaygroundPage })),
);
// APP-004: the API Keys page is now a real protected page (not a placeholder).
// It is code-split off the first-paint path, like the other dashboard features.
const ApiKeysPage = lazy(() =>
  import('@/features/api-keys/ApiKeysRoute').then((m) => ({ default: m.ApiKeysPage })),
);
// APP-003: the Usage page is now a real protected page (not a placeholder). It
// is code-split off the first-paint path, like the other dashboard features.
const UsagePage = lazy(() =>
  import('@/features/usage/UsagePage').then((m) => ({ default: m.UsagePage })),
);
// APP-006: the Billing page is now a real protected page (not a placeholder).
// It is code-split off the first-paint path, like the other dashboard features.
const BillingPage = lazy(() =>
  import('@/features/billing/BillingRoute').then((m) => ({ default: m.BillingPage })),
);
const BulkPlaceholder = lazy(() =>
  import('@/features/bulk/BulkRoute').then((m) => ({ default: m.BulkPage })),
);
const WebhooksPlaceholder = lazy(() =>
  import('@/features/webhooks/WebhooksRoute').then((m) => ({ default: m.WebhooksPage })),
);
// APP-009: the Account page is now a real protected page (not a placeholder).
// It is code-split off the first-paint path, like the other dashboard features.
const AccountPage = lazy(() =>
  import('@/features/account/AccountRoute').then((m) => ({ default: m.AccountPage })),
);
// Public playground: anonymous pre-signup surface. Code-split like the other
// feature routes; lives OUTSIDE <ProtectedRoute/> (sibling of /sign-in) because
// the visitor has no session. Signed-in visitors are redirected to /playground
// by the page itself.
const PublicPlaygroundPage = lazy(() =>
  import('@/features/public-playground/PublicPlaygroundRoute').then((m) => ({ default: m.PublicPlaygroundPage })),
);

function PlaceholderFallback() {
  return <LoadingState label="Loading…" />;
}

export const router = createBrowserRouter([
  { path: '/', element: <RootRoute /> },
  { path: '/sign-in', element: <SignInRoute /> },
  { path: '/sign-up', element: <SignUpRoute /> },
  { path: '/privacy', element: <LegalRedirectRoute kind="privacy" /> },
  { path: '/terms', element: <LegalRedirectRoute kind="terms" /> },
  {
    // Anonymous pre-signup playground: no dashboard shell, no account chrome.
    // Signed-in visitors are redirected to the protected /playground.
    path: '/public/playground',
    element: (
      <Suspense fallback={<PlaceholderFallback />}>
        <PublicPlaygroundPage />
      </Suspense>
    ),
  },
  {
    element: <ProtectedRoute />,
    children: [
      { path: '/onboarding', element: <OnboardingRoute /> },
      { path: '/dashboard', element: <DashboardRoute /> },
      {
        path: '/playground',
        element: (
          <Suspense fallback={<PlaceholderFallback />}>
            <PlaygroundPage />
          </Suspense>
        ),
      },
      {
        path: '/api-keys',
        element: (
          <Suspense fallback={<PlaceholderFallback />}>
            <ApiKeysPage />
          </Suspense>
        ),
      },
      {
        path: '/usage',
        element: (
          <Suspense fallback={<PlaceholderFallback />}>
            <UsagePage />
          </Suspense>
        ),
      },
      {
        path: '/bulk',
        element: (
          <Suspense fallback={<PlaceholderFallback />}>
            <BulkPlaceholder />
          </Suspense>
        ),
      },
      {
        path: '/webhooks',
        element: (
          <Suspense fallback={<PlaceholderFallback />}>
            <WebhooksPlaceholder />
          </Suspense>
        ),
      },
      {
        path: '/billing',
        element: (
          <Suspense fallback={<PlaceholderFallback />}>
            <BillingPage />
          </Suspense>
        ),
      },
      {
        path: '/account',
        element: (
          <Suspense fallback={<PlaceholderFallback />}>
            <AccountPage />
          </Suspense>
        ),
      },
    ],
  },
  // Clerk catch-all auth routes redirect to the canonical sign-in.
  { path: '/sign-in/*', element: <Navigate to="/sign-in" replace /> },
  { path: '/sign-up/*', element: <Navigate to="/sign-up" replace /> },
  { path: '*', element: <NotFoundRoute /> },
]);
